import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './DiagramList.css';

const getToken = () => localStorage.getItem('notes_token');

// 空的 draw.io XML 模板（用于新建流程图初始化空白画布）
const EMPTY_XML = '<mxGraphModel><root><mxCell id="0"/><mxCell id="1" parent="0"/></root></mxGraphModel>';

const apiFetch = async (url, opts = {}) => {
  const token = getToken();
  if (!opts.headers) opts.headers = {};
  if (token) opts.headers['x-token'] = token;
  const res = await fetch(url, opts);
  if (res.status === 401) {
    localStorage.removeItem('notes_token');
    localStorage.removeItem('notes_user');
    window.location.href = '/login';
    return res;
  }
  return res;
};

function DiagramEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const editorReadyRef = useRef(false);
  const xmlDataRef = useRef('');
  const autoSaveTimerRef = useRef(null);
  const [diagram, setDiagram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [xmlData, setXmlData] = useState('');
  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => {
    editorReadyRef.current = editorReady;
  }, [editorReady]);

  useEffect(() => {
    xmlDataRef.current = xmlData;
  }, [xmlData]);

  // 使用 useCallback 包装 loadDiagram
  const loadDiagram = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/diagrams/${id}`);
      if (!res.ok) {
        alert('流程图不存在或无权访问');
        navigate('/diagrams');
        return;
      }
      const data = await res.json();
      setDiagram(data.data);

      // 加载 XML
      const xmlRes = await apiFetch(`/api/diagrams/${id}/xml`);
      if (xmlRes.ok) {
        const xmlDataRes = await xmlRes.json();
        // 如果后端返回空字符串，使用空 XML 模板初始化空白画布
        const loadedXml = xmlDataRes.xmlData || EMPTY_XML;
        setXmlData(loadedXml);
        console.log('XML loaded, length:', loadedXml.length, 'editorReady:', editorReadyRef.current);
        
        // 如果编辑器已经初始化，发送XML数据（始终发送，包括空XML）
        if (editorReadyRef.current && iframeRef.current) {
          console.log('Sending XML to initialized editor');
          iframeRef.current.contentWindow.postMessage(JSON.stringify({
            action: 'load',
            xml: loadedXml,
            autosave: 1
          }), '*');
        }
      }
    } catch (e) {
      console.error('加载失败:', e);
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadDiagram();
  }, [loadDiagram]);

  // draw.io postMessage 通信
  useEffect(() => {
    const handler = async (evt) => {
      if (!iframeRef.current || evt.source !== iframeRef.current.contentWindow) return;
      
      try {
        const msg = JSON.parse(evt.data);
        
        if (msg.event === 'init') {
          console.log('draw.io initialized, xmlData:', xmlDataRef.current ? 'loaded' : 'empty');
          setEditorReady(true);
          // 编辑器初始化完成，始终发送XML数据（包括空XML模板）
          const xmlToSend = xmlDataRef.current || EMPTY_XML;
          if (iframeRef.current) {
            console.log('Sending XML to newly initialized editor, length:', xmlToSend.length);
            iframeRef.current.contentWindow.postMessage(JSON.stringify({
              action: 'load',
              xml: xmlToSend,
              autosave: 1
            }), '*');
          }
        }
        
        if (msg.event === 'autosave' || msg.event === 'save') {
          // draw.io自动保存事件，同步到后端
          const newXml = msg.xml;
          console.log('[draw.io] 收到', msg.event, '事件, xml长度:', newXml ? newXml.length : 'null');
          if (newXml) {
            setXmlData(newXml);
            setDirty(true);
            // 自动保存，加防抖（2秒内只保存一次）
            if (autoSaveTimerRef.current) {
              clearTimeout(autoSaveTimerRef.current);
            }
            autoSaveTimerRef.current = setTimeout(async () => {
              setSaving(true);
              try {
                const res = await apiFetch(`/api/diagrams/${id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ xmlData: newXml })
                });
                if (res.ok) {
                  setDirty(false);
                  console.log('[draw.io] 自动保存成功');
                } else {
                  console.error('[draw.io] 自动保存失败:', res.status);
                }
              } catch (e) {
                console.error('[draw.io] 自动保存失败:', e);
              }
              setSaving(false);
            }, 2000);
          }
        }
        
        if (msg.event === 'export') {
          // 导出图片
          const dataUrl = msg.data;
          if (dataUrl) {
            const link = document.createElement('a');
            link.download = `${diagram?.name || 'diagram'}.png`;
            link.href = dataUrl;
            link.click();
          }
        }
      } catch (e) {
        // ignore non-JSON messages
      }
    };
    
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [diagram, id]);

  // 监听编辑变化，标记为dirty
  useEffect(() => {
    if (!editorReady || !iframeRef.current) return;
    
    const changeHandler = (evt) => {
      if (!iframeRef.current || evt.source !== iframeRef.current.contentWindow) return;
      try {
        const msg = JSON.parse(evt.data);
        if (msg.event === 'autosave' || msg.event === 'save') {
          setDirty(true);
        }
      } catch (e) {}
    };
    
    window.addEventListener('message', changeHandler);
    return () => window.removeEventListener('message', changeHandler);
  }, [editorReady]);

  const handleExport = () => {
    if (iframeRef.current && editorReady) {
      iframeRef.current.contentWindow.postMessage(JSON.stringify({
        action: 'export',
        format: 'png',
        scale: 2
      }), '*');
    }
  };

  const handleBack = () => {
    if (dirty) {
      if (confirm('有未保存的更改，确定离开？')) {
        navigate('/diagrams');
      }
    } else {
      navigate('/diagrams');
    }
  };

  if (loading) {
    return <div className="diagram-editor-loading">加载中...</div>;
  }

  // embed模式URL，隐藏draw.io自带保存按钮
  const drawioUrl = `/drawio/?embed=1&ui=atlas&proto=json&lang=zh&splash=0&noSaveBtn=1`;

  return (
    <div className="diagram-editor-page">
      <div className="diagram-editor-toolbar">
        <button onClick={handleBack} className="btn-back">← 返回</button>
        <span className="diagram-title">{diagram?.name || '未命名'}</span>
        <div className="toolbar-actions">
          <span className="autosave-status">
            {saving ? '💾 保存中...' : dirty ? '● 未保存' : '✓ 已保存'}
          </span>
          <button onClick={handleExport} className="btn-secondary" title="导出为PNG图片" disabled={!editorReady}>
            📷 导出图片
          </button>
        </div>
      </div>
      <div className="diagram-editor-container">
        <iframe
          ref={iframeRef}
          src={drawioUrl}
          title="流程图编辑器"
          className="drawio-iframe"
        />
        <style>{`
          /* 隐藏draw.io自带的保存按钮 */
          .geEditor .geMenubar .geItem:contains("保存"),
          .geEditor .geMenubar a[title*="保存"],
          .geEditor .geMenubar a[title*="Save"] {
            display: none !important;
          }
        `}</style>
      </div>
    </div>
  );
}

export default DiagramEditor;
